import React, { useEffect, useRef, useState } from "react";
import { X, ArrowUp } from "lucide-react";
import { api } from "@/lib/api";
import { Bubba } from "@/lib/Bubba";

const SUGGESTIONS = ["What's a quick 30g protein dinner?", "Can I take my meds during my fast?", "I have knee pain — swap my squats?"];

export default function Coach({ onClose }) {
  const [persona, setPersona] = useState("buddy");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { api("/profile").then((p) => p?.ai_persona && setPersona(p.ai_persona)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim(); if (!msg || sending) return;
    setInput(""); setMessages((m) => [...m, { role: "user", content: msg }]); setSending(true);
    try { const r = await api("/coach/chat", { method: "POST", body: { message: msg, session_id: sessionId, persona } });
      setSessionId(r.session_id); setMessages((m) => [...m, { role: "assistant", content: r.reply }]); }
    catch { setMessages((m) => [...m, { role: "assistant", content: "I'm having trouble right now — try again in a sec!" }]); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div data-testid="coach-screen" className="relative w-full max-w-md bg-[#ECE4D6] border-l border-[#E3D9C8] flex flex-col h-full">
        <div className="flex items-center justify-between p-4 border-b border-[#E3D9C8]">
          <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-white overflow-hidden flex items-center justify-center"><Bubba mood="Happy" size={38} /></div>
            <div><p className="font-extrabold">Ask Bubba</p><p className="text-xs text-[#651F33] font-semibold">{persona === "buddy" ? "Raccoon Buddy" : "Clinical Coach"}</p></div></div>
          <button onClick={onClose}><X className="text-slate-500" /></button>
        </div>
        <div className="flex gap-2 p-3">
          <button data-testid="coach-persona-buddy" onClick={() => setPersona("buddy")} className={`flex-1 py-2 rounded-full text-sm font-semibold border ${persona === "buddy" ? "border-[#651F33] bg-[#651F33]/10 text-[#651F33]" : "border-[#E3D9C8] text-slate-500"}`}>Buddy</button>
          <button data-testid="coach-persona-clinical" onClick={() => setPersona("clinical")} className={`flex-1 py-2 rounded-full text-sm font-semibold border ${persona === "clinical" ? "border-[#651F33] bg-[#651F33]/10 text-[#651F33]" : "border-[#E3D9C8] text-slate-500"}`}>Clinical</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="flex flex-col items-center gap-3"><Bubba mood="Cheering" size={110} />
              <p className="text-slate-500 text-sm text-center max-w-xs">Hey! Ask me anything about your food, meds, fasting or workouts.</p>
              {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="w-full text-sm text-slate-800 bg-white border border-[#E3D9C8] rounded-full px-4 py-2.5 text-center">{s}</button>)}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[82%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-[#651F33] text-[#F5F0E8] rounded-br-sm" : "bg-white border border-[#E3D9C8] text-slate-900 rounded-bl-sm"}`}>{m.content}</div>
            </div>
          ))}
          {sending && <div className="flex justify-start"><div className="bg-white border border-[#E3D9C8] px-4 py-3 rounded-2xl"><div className="w-5 h-5 border-2 border-[#651F33] border-t-transparent rounded-full animate-spin" /></div></div>}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-[#E3D9C8] flex gap-2">
          <input data-testid="coach-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Message Bubba…"
            className="flex-1 bg-white border border-[#E3D9C8] rounded-full px-4 py-3 text-slate-900 outline-none focus:border-[#651F33]" />
          <button data-testid="coach-send-button" onClick={() => send()} disabled={sending} className="w-12 rounded-full bg-[#651F33] text-[#F5F0E8] flex items-center justify-center"><ArrowUp size={20} /></button>
        </div>
      </div>
    </div>
  );
}
